import { faArrowDown } from '@fortawesome/pro-regular-svg-icons/faArrowDown';
import { faArrowUp } from '@fortawesome/pro-regular-svg-icons/faArrowUp';
import { faMinus } from '@fortawesome/pro-regular-svg-icons/faMinus';
import { QBox, QText, QStack, QIcon } from 'quotient/vNext';
import { ResponsiveContainer } from 'recharts';

import { SummaryCardProps, MetricDatum, NumberFormat } from '../../types';
import { formatNumber, formatFieldNameWithAggregation } from '../../utils/chartUtils';
import { BarChart } from '../BarChart/BarChart';
import { LineChart } from '../LineChart/LineChart';

// Simple data conversion - just pass the raw data to chart components
const convertSparklineData = (data: MetricDatum[], xAxisLabel?: string) => {
  if (!data || data.length === 0) return null;

  const firstRow = data[0];

  // Find the X-axis field (the non-summary field that's not 'index')
  const xAxisKey = Object.keys(firstRow).find((key) => key !== 'index' && !key.startsWith('_summary')) || 'x';

  // Get the Y-axis field from the summary data
  const yAxisKey = firstRow._summary_field || 'value';

  // Format Y-axis label using utility function
  const yAxisLabel = formatFieldNameWithAggregation(yAxisKey);

  return {
    data,
    xAxisKeys: [xAxisKey],
    yAxisKeys: [yAxisKey],
    yAxisLabels: [yAxisLabel],
    xAxisLabels: [xAxisLabel || 'Category'],
  };
};

// Returns the JSX component for a supporting number
const supportingNumberComponent = (
  compareAs: 'number' | 'percentage',
  value: number,
  label: string,
  mainValue: number,
  format: NumberFormat | undefined,
) => {
  if (!value) return '';
  const isPositive = Number(value) < mainValue;
  const isNegative = Number(value) > mainValue;
  let difference: number;
  let numberFormat: NumberFormat;
  if (compareAs === 'percentage') {
    difference = Math.abs(Number(value) - mainValue) / mainValue;
    numberFormat = { type: 'percentage' };
  } else {
    difference = Math.abs(Number(value) - mainValue);
    numberFormat = format || { type: 'number' };
  }
  const formattedDifference = formatNumber({ value: difference, format: numberFormat });
  let color;
  let compareAsIcon;
  let backgroundColor;
  if (isPositive) {
    color = 'brand.primary.500';
    backgroundColor = 'brand.primary.50';
    compareAsIcon = faArrowUp;
  } else if (isNegative) {
    color = 'denotive.danger.600';
    backgroundColor = 'brand.quaternary.50';
    compareAsIcon = faArrowDown;
  } else {
    color = 'neutral.primary.600';
    compareAsIcon = faMinus;
    backgroundColor = 'neutral.primary.50';
  }
  return (
    <QStack
      alignItems="center"
      direction="row"
      key={label}
      spacing={1}
      sx={{ color, backgroundColor, borderRadius: '4px', width: 'fit-content', padding: '2px' }}
    >
      {compareAsIcon && <QIcon icon={compareAsIcon} />}
      <QText key={label}>{formattedDifference}</QText>
      <QText>{label}</QText>
    </QStack>
  );
};

export const SummaryCard = ({
  title,
  data,
  format,
  sparkline,
  xAxisLabel,
  showTitle = false,
  onClick,
  supportingNumbers,
  ...props
}: SummaryCardProps) => {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      onClick({}, event);
    }
  };

  // Extract the metric value from the data
  const value = data.length > 0 ? Number(data[0]._summary_value) : 0;

  // Format the value using formatNumber
  const metric = value !== 0 || data.length > 0 ? formatNumber({ value, format, maxDecimals: 2 }) : ''; // Cap at 2 decimal places

  // Generate sparkline data if enabled
  // Check if sparkline is enabled OR if we have X-axis data (fallback detection)
  const hasSparklineData = sparkline?.enabled || (data.length > 0 && 'x' in data[0]);
  const sparklineConfig = hasSparklineData ? convertSparklineData(data, xAxisLabel) : null;

  return (
    <QBox
      data-testid="summary-card"
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        padding: 1.5,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '80px', // Smaller minimum height for 2x2 cards
        containerType: 'size', // Enable container queries for both width and height
        '& .summary-metric': {
          fontSize: 'clamp(2rem, 10cqw, 10rem)', // More aggressive scaling: 8% of container width
          lineHeight: 1,
          fontWeight: 'bold',
          maxWidth: '100%',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          // Fallback for browsers that don't support container queries
          '@supports not (container-type: size)': {
            fontSize: 'clamp(1.5rem, 4vw, 4rem)',
          },
        },
        '& .sparkline-container': {
          height: 'clamp(40px, 35cqh, 120px)', // Increased sparkline height for better visibility
        },
      }}
      onClick={handleClick}
      {...props}
    >
      <QStack direction="column" spacing={3}>
        {showTitle && title && (
          <QText
            color="text.secondary"
            fontWeight="bold"
            maxWidth="100%"
            minWidth={0}
            overflow="hidden"
            textOverflow="ellipsis"
            textStyle="bodyRegular"
            title={title.toLocaleUpperCase()}
            whiteSpace="nowrap"
          >
            {title.toLocaleUpperCase()}
          </QText>
        )}
        <QText className="summary-metric" title={metric}>
          {metric}
        </QText>

        {/* Supporting Numbers */}
        {supportingNumbers &&
          supportingNumbers.length > 0 &&
          supportingNumbers.map((supportingNumber) =>
            supportingNumberComponent(
              supportingNumber.compareAs,
              Number(supportingNumber.value),
              supportingNumber.label,
              value,
              format,
            ),
          )}

        {/* Sparkline */}
        {sparklineConfig && (
          <QBox
            className="sparkline-container"
            sx={{
              width: '100%',
              mt: 1,
            }}
          >
            <ResponsiveContainer height="100%" width="100%">
              {sparkline?.type === 'bar' ? (
                <BarChart
                  data={sparklineConfig.data}
                  hideXAxis
                  hideYAxis
                  scales={{
                    y: {
                      tickFormat: format as NumberFormat,
                    },
                  }}
                  xAxisKeys={sparklineConfig.xAxisKeys}
                  xAxisLabels={sparklineConfig.xAxisLabels}
                  yAxisKeys={sparklineConfig.yAxisKeys}
                  yAxisLabels={sparklineConfig.yAxisLabels}
                />
              ) : (
                <LineChart
                  data={sparklineConfig.data}
                  hideAxes
                  scales={{
                    y: {
                      tickFormat: format as NumberFormat,
                    },
                  }}
                  xAxisKeys={sparklineConfig.xAxisKeys}
                  xAxisLabels={sparklineConfig.xAxisLabels}
                  yAxisKeys={sparklineConfig.yAxisKeys}
                  yAxisLabels={sparklineConfig.yAxisLabels}
                />
              )}
            </ResponsiveContainer>
          </QBox>
        )}
      </QStack>
    </QBox>
  );
};
